import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaProjectDiagram, FaTasks, FaCoins, FaShieldAlt, FaStopwatch, FaSearch, FaArchive } from "react-icons/fa";
import { getArchiveUrl } from "../../services/apiClient.js";
import FavouriteJobChips from "./FavouriteJobChips.jsx";

// The RDR sections as tinted icon chips (Karen's card-header style). Shared by
// the search-page band and the RdrPage section nav so the colour → category
// mapping stays identical everywhere. Predictive + Completions deliberately
// aren't chips — they live in the header Reports dropdown (UserMenu.jsx).
export const RDR_SECTIONS = [
  // "PCO Dashboard" (Karen's feedback doc, Robert agreed 2026-07-15; was
  // "Main" → "Dashboard" on 07-13). Route stays /rdr/main.
  { key: "main", label: "PCO Dashboard", to: "/rdr/main", icon: FaTasks, tint: "green" },
  { key: "accounts", label: "Accounts", to: "/rdr/accounts", icon: FaCoins, tint: "amber" },
  { key: "health-safety", label: "Health & Safety", to: "/rdr/health-safety", icon: FaShieldAlt, tint: "blue" },
  { key: "workflow", label: "Workflow Management", to: "/rdr/workflow", icon: FaProjectDiagram, tint: "purple" },
  { key: "sla", label: "SLA", to: "/rdr/sla", icon: FaStopwatch, tint: "rose" },
  // Login lands on the Dashboard (2026-07-13), so the search needs a door of
  // its own. Slate = navigation-neutral, not one of the category colours.
  //
  // HIDDEN, not removed (Robert, 13 Aug 2026). It was the odd one out: the other
  // five are report SECTIONS, this one was navigation wearing the same clothes,
  // and on the search page it pointed at the page you were already on. The door
  // it provided is not lost — the header title has always linked to the search
  // (app/InfoPanel.jsx) and now carries a magnifying glass so people can SEE
  // that it does, which is what this chip was really compensating for.
  // Delete `hidden` to bring it back.
  { key: "search", label: "Claim Search", to: "/claims/search", icon: FaSearch, tint: "slate", hidden: true },
];

// The archive address, fetched ONCE per page load and shared by every chip row
// on it — a module-level promise, because RdrChips renders on several pages and
// none of them should each pay for the same tiny request. Null URL = no button,
// which is the correct state on dev, the demo, and the archive app itself.
let archiveUrlPromise = null;
const fetchArchiveUrl = () => {
  if (!archiveUrlPromise) archiveUrlPromise = getArchiveUrl().catch(() => "");
  return archiveUrlPromise;
};

function RdrChips({ active }) {
  const [archiveUrl, setArchiveUrl] = useState("");
  useEffect(() => {
    let alive = true;
    fetchArchiveUrl().then((u) => { if (alive) setArchiveUrl(u); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="d-flex align-items-center column-gap-2 flex-wrap no-print">
      {RDR_SECTIONS.filter((s) => !s.hidden).map(({ key, label, to, icon: Icon, tint }) => (
        <Link
          key={key}
          to={to}
          aria-current={key === active ? "page" : undefined}
          className={`btn rdr-chip rdr-chip-${tint}${key === active ? " rdr-chip-active" : ""}`}
        >
          <Icon size={12} aria-hidden="true" />
          {label}
        </Link>
      ))}
      {/* THE ARCHIVE — grey on purpose (Robert 2026-08-21): it is a different
          SYSTEM, not another report section, so it must not wear one of the
          five category colours. Opens in a new tab because it is a separate
          app with its own login and its own banner; leaving this one open is
          almost always what somebody wants. Absent entirely when the setting
          is blank. */}
      {archiveUrl ? (
        <a
          href={archiveUrl}
          target="_blank"
          rel="noreferrer"
          className="btn rdr-chip rdr-chip-slate"
          data-hint="Jobs before #71511 — the archived record, in a separate system"
        >
          <FaArchive size={12} />
          Archive
        </a>
      ) : null}
      {/* Pinned jobs ride the spare room after Archive (Robert 2026-09-02) —
          rendered by the band itself so every page with the band gets them. */}
      <FavouriteJobChips />
    </div>
  );
}

export default RdrChips;
